import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

const INTERVAL_OPTIONS = [
  { value: 1, label: '1 second' },
  { value: 5, label: '5 seconds' },
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 300, label: '5 minutes' },
];

function labelForSeconds(seconds) {
  const known = INTERVAL_OPTIONS.find((o) => o.value === seconds);
  return known ? known.label : `${seconds} seconds`;
}

/**
 * View-and-set card for the sensor's sampling interval (polling). On mount it
 * reads the current interval from the device via getPolling(), preselects it,
 * and lets an operator pick a new interval and persist it via setPolling().
 */
export default function PollingCard({ serialClient, onError = undefined }) {
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const seconds = await serialClient.getPolling();
        if (cancelled) return;
        setCurrent(seconds);
        setSelected(seconds);
      } catch (err) {
        if (cancelled) return;
        const message = err.message || 'Failed to read sampling interval';
        setError(message);
        if (onError) onError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serialClient, onError]);

  const handleSave = useCallback(async () => {
    setBusy(true);
    setSuccess(false);
    setError(null);
    try {
      await serialClient.setPolling(selected);
      setCurrent(selected);
      setSuccess(true);
    } catch (err) {
      const message = err.message || 'Failed to save sampling interval';
      setError(message);
      if (onError) onError(message);
    } finally {
      setBusy(false);
    }
  }, [serialClient, onError, selected]);

  const unchanged = selected === current;

  const options = INTERVAL_OPTIONS.slice();
  if (current !== null && !INTERVAL_OPTIONS.some((o) => o.value === current)) {
    options.push({ value: current, label: labelForSeconds(current) });
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Polling
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How often the sensor takes a reading.
        </Typography>

        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Current: {current !== null ? labelForSeconds(current) : 'unknown'}
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="polling-interval-label">Sampling Interval</InputLabel>
              <Select
                labelId="polling-interval-label"
                label="Sampling Interval"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
              >
                {options.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={busy || unchanged}
            >
              {busy ? 'Saving...' : 'Save'}
            </Button>
          </>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Sampling interval saved.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

PollingCard.propTypes = {
  serialClient: PropTypes.object.isRequired,
  onError: PropTypes.func,
};
